import { useEffect, useRef } from 'react';
import _ from 'lodash';

import { routes } from '../constants';
import { SearchResultsTab } from '../types/search';
import queryToSearchFilters from './queryToSearchFilters';
import searchResultsStorageManager from './searchResultsStorageManager';
import useRouter from './useRouter';

const isKnownTab = (tab: string): tab is SearchResultsTab =>
  (Object.values(SearchResultsTab) as string[]).includes(tab);

/**
 * Bridges per-tab sessionStorage gaps for pages opened in a new browser tab from search
 * (open-in-new-tab experiment). Reads `search_id` and `from` from the URL, rebuilds
 * sessionStorage entries (searchId / tabKey / filters) so `getSearchPageUrl()` and
 * `getCurrentSearchId()` return the originating values in the new tab, then strips `from`
 * from the URL so it doesn't linger on subsequent navigations or shared links.
 */
const useHydrateSearchStateFromUrl = (): void => {
  const { router, match } = useRouter();
  const didRunRef = useRef(false);

  useEffect(() => {
    if (didRunRef.current) return;
    didRunRef.current = true;

    const { query, pathname } = match.location;
    const rawSearchId = query?.search_id;
    const rawFrom = query?.from;
    const searchIdParam = Array.isArray(rawSearchId) ? rawSearchId[0] : rawSearchId;
    const fromParam = Array.isArray(rawFrom) ? rawFrom[0] : rawFrom;

    // `from` is the authoritative origin-search URL captured at click time.
    // Always overwrite when present: target=_blank links inherit the opener's
    // sessionStorage, which may be stale if the click happened before the
    // opener's reducer wrote the latest filters (SEAR-559 race: term in URL
    // but not yet in lastSearchState).
    if (fromParam) {
      const [fromPath, fromQs] = fromParam.split('?');
      const searchPrefix = `/${routes.search.root}/`;
      if (fromPath.startsWith(searchPrefix)) {
        const tab = fromPath.slice(searchPrefix.length);
        if (isKnownTab(tab)) {
          searchResultsStorageManager.setSearchTabKey(tab);
        }
        searchResultsStorageManager.setSearchFilters(queryToSearchFilters(fromQs ? `?${fromQs}` : ''));
      }

      router.replace({ pathname, query: _.omit(query, 'from') });
    }

    if (searchIdParam) {
      searchResultsStorageManager.setSearchId(searchIdParam);
    }
    // Hydration is one-shot: re-running would be a no-op (didRunRef + populated storage)
    // or conflict with the router.replace() URL change above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useHydrateSearchStateFromUrl;
